/**
 * AI Context Templates for CreateTripV2 Page
 * 
 * This file contains simplified context templates for the new CreateTripV2 page.
 * Based on v1 template structure from aiContextTemplates.js
 */

import { getLanguageName } from './utils.js';

// System context templates
const systemContextTemplateString = {
  tripPlannerAssistant: "You are a helpful AI assistant that specializes in travel planning. return only real data!!!!. Return only valid JSON based on the instructions. make sure the result string starts with { and ends with }. Make sure you give json that can be copy and paste to json viewer - https://jsonviewer.stack.hu/.",
  jsonValidator: "You are a JSON validation expert. Check the provided JSON for errors and fix them. Make sure you give json that can be copy and paste to json viewer - https://jsonviewer.stack.hu/. do not change the type of the properties. Return only valid JSON with no additional text. make sure the result string starts with { and ends with }",
  jsonValidatorExtractor: "You are a JSON validation expert. Check the provided text and look for a valid JSON. if you find you need to extract it and reuturn only the json!!!. Make sure you give json that can be copy and paste to json viewer - https://jsonviewer.stack.hu/. do not change the type of the properties. Return only valid JSON with no additional text. make sure the result string starts with { and ends with }"
};

// Template for pre user data check
const preUserDataCheckTemplateString = `
You are a travel input analyzer. 
Analyze the user's input and determine if it contains the necessary trip information.
For each category, provide a detailed analysis with specific examples found in the text.

Return ONLY this JSON structure:
{
  "tripLocations": {
    "found": true|false,
    "examples": ["list of locations mentioned"],
    "confidence": 0-100 (percentage of confidence),
    "missing": "what information is missing or suggestions"
  }  
}
Return ONLY this JSON structure with no additional text.
Make sure the result string starts with { and ends with }.
`;

// Template for basic plan information (Step 1)
const getBasicPlanTemplate = (language = 'en') => {
  const languageName = getLanguageName(language);
  return `
You are a travel plan helper. 
Based on the user's input, create a JSON object with trip information according to the json given below as an example.

Return ONLY this JSON structure:
{
  "name": "Trip name based on destination",
  "nameInEnglish": "Trip name based on destination in English",
  "description": "a detailed description about the destination of the trip. At least two sentences."
}

IMPORTANT: All JSON values should be in this language - ${languageName} (language code: ${language}), unless a field is specifically marked as "InEnglish" (like nameInEnglish), which should always be in English.

Return ONLY this JSON structure!!!!! no text or other information oustide!!! 
Make sure you give json that can be copy and paste to json viewer - https://jsonviewer.stack.hu/. do not change the type of the properties. Return only valid JSON with no additional text. make sure the result string starts with { and ends with }"
  `;
};

// Template for accommodations (Step 2)
const getAccommodationsTemplate = (language = 'en', numAccommodations = 1) => {
  const languageName = getLanguageName(language);
  return `
You are a travel plan helper. 
Based on the user's input, create a JSON object with accommodations details.
If the user doesn't mention any specific accommodations, return an empty array.
I need at least ${numAccommodations} accommodation option${numAccommodations > 1 ? 's' : ''}!
json object should be according to the json given below as an example.

Return ONLY this JSON structure:
{
  "Accommodations": [
    {
      "id": "<generate guid>",
      "name": "Iמ ${languageName} : Accommodation name - Translated to ${languageName}",
      "nameInEnglish": "In English : Accommodation name - translated to English",
      "description": "Iמ ${languageName} : full description of the accommodation - Translated to ${languageName}",
      "location": {
        "address": "Iמ ${languageName} :full address of the accommodation! including street, city, state, country, zip code, etc. - Translated to ${languageName}",
        "addressInEnglish": "In English :full address of the accommodation translated to English! including street, city, state, country, zip code, etc.",
        "city": "Iמ ${languageName} :city where the accommodation is located",
        "cityInEnglish": "city where the accommodation is located in English",
        "country": "Iמ ${languageName} :country where the accommodation is located",
        "countryInEnglish": "country where the accommodation is located in English",
        "latitude": "latitude of the accommodation",
        "longitude": "longitude of the accommodation"
     },
      "website": "A valid Website URL of the accommodation incluing http or https. Only real website is allowed!!",
      "checkInTime": "The checkin time in the accommodation in this format : 15:00",
      "checkOutTime": "The checkout time in the accommodation in this format : 11:00",
      "startDate": "<ISO date string>",
      "endDate": "<ISO date string>"
    }
  ]
}

IMPORTANT: All JSON values should be in this language - ${languageName} (language code: ${language}), unless a field is specifically marked as "InEnglish" (like nameInEnglish, addressInEnglish, cityInEnglish, countryInEnglish), which should always be in English.
`;
};

// Template for transportation (Step 3)
const getTransportationsTemplate = (language = 'en') => {
  const languageName = getLanguageName(language);
  return `
You are a travel plan helper.
Based on the user's input, create a JSON object with transportation details.

Transportation extraction rules:
Extract transportation that the user explicitly mentions (flights, trains, buses, taxis, etc.).
If the user does not mention the transportation type but clearly specifies both the origin and destination, infer the most appropriate transportation type using real-world logic.
If neither an explicit transportation nor a clear origin-destination pair is provided, return an empty array.

Use all information provided by the user and preserve it in the JSON object (flight number, train number, dates, times, etc.).
regarding the links array : add links to all relevent websites (flight, train, airport, flight radar, etc.)
flight radar link should be to the real flight with the flight number in this format : https://www.flightradar24.com/data/flights/<real flight number here>!

json object should be according to the json given below as an example.
Return ONLY this JSON structure:
{
  "Transportations": [
    {
      "id": "<generate guid>",
      "name": "Transportation name or flight number",
      "nameInEnglish": "In English : Transportation name or flight number - translated to English",
      "description": "full description of the transportation",
      "fromLocation": {
        "address": "Iמ ${languageName} :full address of the origin location - airport, train station, etc. - Translated to ${languageName}",
        "addressInEnglish": "In English : full address of the origin location translated to English - airport, train station, etc.",
        "city": "Iמ ${languageName} :city where the origin location is located",
        "cityInEnglish": "city where the origin location is located in English",
        "country": "Iמ ${languageName} :country where the origin location is located",
        "countryInEnglish": "country where the origin location is located in English",
        "latitude": "latitude of the origin location",
        "longitude": "longitude of the origin location"
     },
     "toLocation": {
        "address": "Iמ ${languageName} :full address of destination location - airport, train station, etc. - Translated to ${languageName}",
        "addressInEnglish": "In English : full address of destination location translated to English - airport, train station, etc.",
        "city": "Iמ ${languageName} :city where the destination location is located",
        "cityInEnglish": "city where the destination location is located in English",
        "country": "Iמ ${languageName} :country where the destination location is located",
        "countryInEnglish": "country where the destination location is located in English",
        "latitude": "latitude of the destination location",
        "longitude": "longitude of the destination location"
     },
      "estimatedDuration": "Duration in hours or minutes in this format: hh:mm",      
      "startDate": "<ISO date string>",
      "endDate": "<ISO date string>",      
      "links": [
        {
          "name": "Information type",
          "url": "URL to resource including http or https"
        }
      ]
    }
  ]
}

IMPORTANT: All JSON values should be in this language - ${languageName} (language code: ${language}), unless a field is specifically marked as "InEnglish" (like nameInEnglish, addressInEnglish, cityInEnglish, countryInEnglish), which should always be in English.

Return ONLY this JSON structure!!!!! no text or other information oustide!!! 

`;
};

// Template for attractions (Step 4)
const getAttractionsTemplate = (language = 'en', numAttractions = 20) => {
  const languageName = getLanguageName(language);
  return `
You are a travel plan helper. 
Based on the user's input, create a JSON object with attractions details.

If the user doesn't mention any specific attractions, return an empty array.

Consider the preferences, group composition, and ages mentioned by the user when selecting attractions.
I need at least ${numAttractions} top attractions!

json object should be according to the json given below as an example.
Return ONLY this JSON structure:
{
  "suggestedAttractions": [
    {
      "id" : "<generate guid>",
      "name" : "Attraction name",
      "nameInEnglish" : "In English : Attraction name translated to English",
      "description" : "full description of the attraction. Try to give at least 2 sentences",
      "location": {
        "address": "Iמ ${languageName} :full address of the attraction - Translated to ${languageName}",
        "addressInEnglish": "In English : full address of the attraction translated to English",
        "city": "Iמ ${languageName} :city where the attraction is located",
        "cityInEnglish": "city where the attraction is located in English",
        "country": "Iמ ${languageName} :country where the attraction is located",
        "countryInEnglish": "country where the attraction is located in English",
        "latitude": "latitude of the attraction. Do not guess! if not sure, use the addressInEnglish to get the latitude from google maps.",
        "longitude": "longitude of the attraction. Do not guess! if not sure, use the addressInEnglish to get the longitude from google maps."
     },
      "estimatedDuration": "Duration in hours or minutes in this format: hh:mm",
      "price": "Price information if available",
      "website": "Website URL if available including http or https"
    }
  ]
}

IMPORTANT: All JSON values should be in this language - ${languageName} (language code: ${language}), unless a field is specifically marked as "InEnglish" (like nameInEnglish, addressInEnglish, cityInEnglish, countryInEnglish), which should always be in English.
  
Return ONLY this JSON structure!!!!! no text or other information oustide!!! 
`;
};

// Template for user-entered attractions with dates
const getUserAttractionsTemplate = (language = 'en') => {
  const languageName = getLanguageName(language);
  return `
You are a travel plan helper specialized in extracting attraction information.
Analyze the user's input and identify SPECIFIC attractions that the user wants to visit, along with their dates if mentioned.
Do NOT include general attraction types or categories - only extract attractions the user explicitly mentions wanting to visit.

For each attraction identified:
1. Extract the specific attraction name (e.g., "Eiffel Tower", "Louvre Museum")
2. If the user mentioned a specific date to visit, include that date
3. If the user mentioned an estimated duration for the visit, include that

Return ONLY this JSON structure:
{
  "userAttractions": [
    {
      "id": "<generate guid>",
      "name": "Iמ ${languageName} : Attraction name - Translated to ${languageName} ",
      "nameInEnglish": "In English : Attraction name translated to English",
      "description": "full description of the attraction. Try to give at least 2 sentences",
      "location": {
       "address": "Iמ ${languageName} :full address of the attraction - Translated to ${languageName}",
        "addressInEnglish": "full address of the attraction in English",
        "city": "Iמ ${languageName} :city where the attraction is located",
        "cityInEnglish": "city where the attraction is located in English",
        "country": "Iמ ${languageName} :country where the attraction is located",
        "countryInEnglish": "country where the attraction is located in English",
        "latitude": "latitude of the attraction. Do not guess! if not sure, use the addressInEnglish to get the latitude from google maps.",
        "longitude": "longitude of the attraction. Do not guess! if not sure, use the addressInEnglish to get the longitude from google maps."
      },
      "estimatedDuration": "Duration in hours or minutes in this format: hh:mm",
      "price": "Price information if available",
      "website": "Website URL if available including http or https",
      "startDate": "<ISO date string based on when user wants to visit, if specified! do not guess the date!! if you are not sure just leave it blank>",
      "endDate": "<ISO date string, typically same as startDate unless multi-day! do not assume or guess the date!! if you are not sure just leave it blank>"
    }
  ]
}

IMPORTANT: All JSON values should be in this language - ${languageName} (language code: ${language}), unless a field is specifically marked as "InEnglish" (like nameInEnglish, addressInEnglish, cityInEnglish, countryInEnglish), which should always be in English.

If NO specific attractions are explicitly mentioned by the user, return an empty array.
Make sure dates are in valid ISO format (YYYY-MM-DDT00:00:00.000Z) if provided.
Return ONLY this JSON structure with no additional text.
`;
};

// Template for generating a full itinerary with attractions for each day
const getFullItineraryTemplate = (language = 'en') => {
  const languageName = getLanguageName(language);
  return `
You are a travel plan helper specializing in creating daily itineraries.
Analyze the user's trip information and create a full daily itinerary with exactly 2 attractions for EACH DAY of the trip.

Consider:
1. The locations mentioned in the trip
2. The dates of the trip
3. Logical travel patterns (e.g., attractions in the same area on the same day)
4. User preferences or interests if mentioned

For each day of the trip, suggest 2 specific attractions to visit:
- Include real, popular, and appropriate attractions for each location
- Estimate a reasonable duration for each attraction visit
- Assign specific dates to each attraction based on the overall trip dates
- Ensure attractions are geographically sensible (don't suggest attractions in different cities on same day)

Return ONLY this JSON structure:
{
  "userAttractions": [
    {
      "id": "<generate guid>",
      "name": "Iמ ${languageName} : Attraction name - Translated to ${languageName} ",
      "nameInEnglish": "In English : Attraction name translated to English",
      "description": "full description of the attraction. Try to give at least 2 sentences",
      "location": {
       "address": "Iמ ${languageName} :full address of the attraction - Translated to ${languageName}",
        "addressInEnglish": "In English : full address of the attraction translated to English",
        "city": "Iמ ${languageName} :city where the attraction is located",
        "cityInEnglish": "city where the attraction is located in English",
        "country": "Iמ ${languageName} :country where the attraction is located",
        "countryInEnglish": "country where the attraction is located in English",
        "latitude": "latitude of the attraction. Do not guess! if not sure, use the addressInEnglish to get the latitude from google maps.",
        "longitude": "longitude of the attraction. Do not guess! if not sure, use the addressInEnglish to get the longitude from google maps."
      },
      "estimatedDuration": "Duration in hours or minutes in this format: hh:mm",
      "price": "Price information if available",
      "website": "Website URL if available including http or https",
      "startDate": "<ISO date string based on when user wants to visit, if specified>",
      "endDate": "<ISO date string, typically same as startDate unless multi-day>"
    },
    ... additional attractions for all days of the trip ...
  ]
}

IMPORTANT: All JSON values should be in this language - ${languageName} (language code: ${language}), unless a field is specifically marked as "InEnglish" (like nameInEnglish, addressInEnglish, cityInEnglish, countryInEnglish), which should always be in English.

IMPORTANT REQUIREMENTS:
1. You MUST create exactly 2 attractions for EACH day of the trip
2. All attractions MUST have startDate and endDate fields properly formatted as ISO date strings
3. Make sure the dates are within the trip date range
4. Each attraction should be appropriate for the location where the user will be on that date
5. Do not create attraction for the first day and last day of the trip.

Return ONLY this JSON structure with no additional text.
`;
};

// Accommodation suggestion prompt
const suggestAccommodationsPromptString = "Please suggest suitable accommodations based on the trip locations. IMPORTANT: For multi-city trips, make sure to adjust the accommodation dates correctly so there are NO OVERLAPPING DATES between different accommodations. Each day should have exactly ONE accommodation. If the user is visiting a location for a specific date range within the overall trip, only create accommodations that match those exact dates.";

// Template function for system context that accepts language parameter
const getTripPlannerAssistantTemplate = (language = 'en') => {
  // language parameter is passed through for future use
  return systemContextTemplateString.tripPlannerAssistant;
};

// Helper functions that accept language parameter and call template functions
const getTripPlannerAssistant = (language = 'en') => {
  return getTripPlannerAssistantTemplate(language);
};

const getBasicPlan = (language = 'en') => {
  return getBasicPlanTemplate(language);
};

const getFullItinerary = (language = 'en') => {
  return getFullItineraryTemplate(language);
};

const getAccommodations = (language = 'en', numAccommodations = 1) => {
  return getAccommodationsTemplate(language, numAccommodations);
};

const getTransportations = (language = 'en', numTransportations = 1) => {
  return getTransportationsTemplate(language, numTransportations);
};

const getAttractions = (language = 'en', numAttractions = 20) => {
  return getAttractionsTemplate(language, numAttractions);
};

const getUserAttractions = (language = 'en') => {
  return getUserAttractionsTemplate(language);
};

// Export all templates in v1 structure
export const createTripTemplatesV2 = {
  systemContext: {
    tripPlannerAssistant: systemContextTemplateString.tripPlannerAssistant,
    getTripPlannerAssistant: getTripPlannerAssistant,
    jsonValidator: systemContextTemplateString.jsonValidator,
    jsonValidatorExtractor: systemContextTemplateString.jsonValidatorExtractor
  },
  preUserDataCheck: preUserDataCheckTemplateString,
  basicPlan: getBasicPlanTemplate(),
  getBasicPlan: getBasicPlan,
  accommodations: getAccommodationsTemplate(),
  getAccommodations: getAccommodations,
  transportations: getTransportationsTemplate(),
  getTransportations: getTransportations,
  attractions: getAttractionsTemplate(),
  getAttractions: getAttractions,
  userAttractions: getUserAttractionsTemplate(),
  getUserAttractions: getUserAttractions,
  fullItinerary: getFullItineraryTemplate(),
  getFullItinerary: getFullItinerary,
  suggestAccommodationsPrompt: suggestAccommodationsPromptString,
  
  // Dynamic template getters
  getAttractionsTemplate: (language = 'en', numAttractions = 20) => {
    return getAttractionsTemplate(language, numAttractions);
  }
};
